import type {
  ApiResponse,
  Membership,
  MembershipAddon,
  MembershipSeatType,
  MembershipType,
} from '@court-sync/shared';
import { apiGet, apiPost, isApiError, publicApiGet, publicApiClient } from './apiClient';
import type { MembershipPurchaseResponse, MembershipQuoteBreakdown } from './clubMembers';

export interface PublicMembershipClub {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
}

export interface PublicMembershipCatalog {
  club: PublicMembershipClub;
  membershipTypes: MembershipType[];
  addons: MembershipAddon[];
}

export interface PublicMembershipSeatInput {
  firstName: string;
  lastName: string;
  /** `YYYY-MM-DD`, or omitted when unknown. */
  dateOfBirth?: string | null;
  /** When set, the join flow auto-links to an existing active member at this club. */
  email?: string | null;
}

export type PublicMembershipMemberEmailLookupResult =
  | { match: false }
  | { match: true; member: { id: string; firstName: string; lastName: string } };

export interface PublicMembershipAddonSelectionInput {
  addonId: string;
  quantity?: number;
}

export interface PublicMembershipPrimaryMemberInput {
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth?: string | null;
  phone?: string;
}

export interface PublicMembershipQuoteRequest {
  membershipTypeId: string;
  primaryMember?: PublicMembershipPrimaryMemberInput;
  additionalSeats: PublicMembershipSeatInput[];
  addonSelections: PublicMembershipAddonSelectionInput[];
}

export interface PublicMembershipQuoteResponse {
  breakdown: MembershipQuoteBreakdown;
  taxAmountCents?: number;
  taxInclusive?: boolean;
  period: { startDate: string; endDate: string | null; seasonLabel: string | null };
  requiresVerification: boolean;
  verificationReasons: string[];
}

export interface MemberPortalMembershipSeat {
  memberId: string;
  firstName: string;
  lastName: string;
  seatType: MembershipSeatType;
  isPrimary: boolean;
}

export interface MemberPortalAddonSelection {
  addonId: string;
  name: string;
  quantity: number;
  totalCents: number;
}

export interface MemberPortalMembership {
  id: string;
  status: Membership['status'];
  termType: Membership['termType'];
  startDate: string;
  endDate: string | null;
  seasonLabel: string | null;
  basePriceCents: number;
  addonsTotalCents: number;
  surchargeCents: number;
  totalCents: number;
  currency: string;
  paymentStatus: Membership['paymentStatus'];
  paymentMethod: Membership['paymentMethod'];
  paidAt: string | null;
  hasReceipt: boolean;
  renewedFromMembershipId: string | null;
  membershipType: {
    id: string;
    name: string;
    termType: MembershipType['termType'];
    courtHireDiscountPercent: number;
  } | null;
  seats: MemberPortalMembershipSeat[];
  addonSelections: MemberPortalAddonSelection[];
}

export interface MemberPortalResponse {
  club: Pick<PublicMembershipClub, 'id' | 'name' | 'slug' | 'logoUrl'>;
  memberships: MemberPortalMembership[];
}

export interface MembershipRenewRequest {
  membershipId: string;
  addonSelections?: PublicMembershipAddonSelectionInput[];
}

/** Generic wrapper matching the clubMembers.ts convention: never throws, always resolves. */
async function safeCall<T>(fn: () => Promise<ApiResponse<T>>, fallbackError: string): Promise<ApiResponse<T>> {
  try {
    return await fn();
  } catch (error: unknown) {
    return {
      success: false,
      error: isApiError(error) ? error.message : error instanceof Error ? error.message : fallbackError,
      ...(isApiError(error) && error.details ? { details: error.details } : {}),
    } as ApiResponse<T>;
  }
}

export const publicClubMembershipService = {
  async getCatalog(clubSlug: string): Promise<ApiResponse<PublicMembershipCatalog>> {
    return safeCall(
      () => publicApiGet<PublicMembershipCatalog>(`/public/club-membership/${clubSlug}`),
      'This club is not available for online membership join'
    );
  },

  async quote(
    clubSlug: string,
    input: PublicMembershipQuoteRequest,
    options?: { authenticated?: boolean }
  ): Promise<ApiResponse<PublicMembershipQuoteResponse>> {
    return safeCall(async () => {
      if (options?.authenticated) {
        return apiPost<PublicMembershipQuoteResponse>(`/public/club-membership/${clubSlug}/quote`, input);
      }
      const response = await publicApiClient.post<ApiResponse<PublicMembershipQuoteResponse>>(
        `/public/club-membership/${clubSlug}/quote`,
        input
      );
      return response.data;
    }, 'Failed to price this membership');
  },

  async lookupMemberEmail(
    clubSlug: string,
    email: string
  ): Promise<ApiResponse<PublicMembershipMemberEmailLookupResult>> {
    return safeCall(
      () =>
        apiPost<PublicMembershipMemberEmailLookupResult>(
          `/public/club-membership/${clubSlug}/members/lookup-email`,
          { email }
        ),
      'Failed to look up member email'
    );
  },

  /** Authenticated — the caller must already have a Firebase session (apiPost attaches the token). */
  async join(clubSlug: string, input: PublicMembershipQuoteRequest): Promise<ApiResponse<MembershipPurchaseResponse>> {
    return safeCall(
      () => apiPost<MembershipPurchaseResponse>(`/public/club-membership/${clubSlug}/join`, input),
      'Failed to join this club'
    );
  },

  /** Authenticated — the member portal's view of the caller's own memberships at this club. */
  async getMyMembership(clubSlug: string): Promise<ApiResponse<MemberPortalResponse>> {
    return safeCall(
      () => apiGet<MemberPortalResponse>(`/public/club-membership/${clubSlug}/my-membership`),
      'Failed to load your membership'
    );
  },

  /** Authenticated — self-service renewal of a fixed-period membership the caller holds a seat on. */
  async renew(
    clubSlug: string,
    input: MembershipRenewRequest
  ): Promise<ApiResponse<MembershipPurchaseResponse>> {
    return safeCall(
      () => apiPost<MembershipPurchaseResponse>(`/public/club-membership/${clubSlug}/renew`, input),
      'Failed to renew your membership'
    );
  },

  /** Authenticated — self-service cancellation for a membership the caller holds a seat on. */
  async cancel(
    clubSlug: string,
    input: { membershipId: string; reason?: string }
  ): Promise<ApiResponse<Membership>> {
    return safeCall(
      () => apiPost<Membership>(`/public/club-membership/${clubSlug}/cancel`, input),
      'Failed to cancel your membership'
    );
  },

  async cancelCheckout(clubSlug: string, membershipId: string): Promise<ApiResponse<null>> {
    return safeCall(
      () =>
        apiPost<null>(`/public/club-membership/${clubSlug}/memberships/${membershipId}/cancel-checkout`, {}),
      'Failed to cancel membership checkout'
    );
  },

  async resumeCheckout(
    clubSlug: string,
    membershipId: string
  ): Promise<ApiResponse<MembershipPurchaseResponse>> {
    return safeCall(
      () =>
        apiPost<MembershipPurchaseResponse>(
          `/public/club-membership/${clubSlug}/memberships/${membershipId}/resume-checkout`,
          {}
        ),
      'Failed to resume membership checkout'
    );
  },

  async getMembershipReceipt(
    clubSlug: string,
    membershipId: string
  ): Promise<ApiResponse<{ receiptUrl: string }>> {
    return safeCall(
      () =>
        apiGet<{ receiptUrl: string }>(
          `/public/club-membership/${clubSlug}/memberships/${membershipId}/receipt`
        ),
      'Failed to load membership receipt'
    );
  },
};
